import React from "react";
import { IOwnerPropertyOption } from "./types";
import {
  PropertySelectionState,
  togglePropertySelection,
  toggleSelectAllProperties,
} from "./utils/propertySelection";

interface PropertyMultiSelectProps {
  properties: IOwnerPropertyOption[];
  selectedIds: number[];
  assignAll: boolean;
  onSelectionChange: (next: PropertySelectionState) => void;
  error?: string;
  disabled?: boolean;
}

export default function PropertyMultiSelect({
  properties,
  selectedIds,
  assignAll,
  onSelectionChange,
  error,
  disabled = false,
}: PropertyMultiSelectProps) {
  const allPropertyIds = properties.map((property) => property.id);

  function handleToggleProperty(propertyId: number) {
    onSelectionChange(
      togglePropertySelection(
        propertyId,
        selectedIds,
        assignAll,
        allPropertyIds
      )
    );
  }

  function handleToggleSelectAll() {
    onSelectionChange(toggleSelectAllProperties(assignAll, allPropertyIds));
  }

  if (properties.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3 text-sm text-[#64748B]">
        Nenhum imóvel disponível para vincular.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="max-h-56 overflow-y-auto rounded-xl border border-[#E2E8F0] bg-white p-3">
        <label
          className={`flex cursor-pointer items-center gap-3 px-2 py-2 text-sm font-semibold text-[#2D3748] ${
            disabled ? "cursor-not-allowed opacity-60" : ""
          }`}
        >
          <input
            type="checkbox"
            className="h-4 w-4 accent-[#DD6435]"
            checked={assignAll}
            disabled={disabled}
            onChange={handleToggleSelectAll}
          />
          <span>Marcar todos os imóveis</span>
        </label>

        <div className="my-1 border-t border-[#E2E8F0]" role="separator" />

        <div className="space-y-1">
          {properties.map((property) => {
            const checked = assignAll || selectedIds.includes(property.id);

            return (
              <label
                key={property.id}
                className={`flex cursor-pointer items-start gap-3 rounded-lg px-2 py-2 text-sm transition-colors hover:bg-[#F8FAFC] ${
                  disabled ? "cursor-not-allowed opacity-60" : ""
                }`}
              >
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 accent-[#DD6435]"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => handleToggleProperty(property.id)}
                />
                <span className="min-w-0 text-[#2D3748]">
                  <span className="font-medium">{property.title}</span>
                  {property.label !== property.title && (
                    <span className="mt-0.5 block text-xs text-[#64748B]">
                      {property.label}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      </div>
      {error && <p className="text-xs text-[#E53E3E]">{error}</p>}
    </div>
  );
}
